/* eslint-disable */

import { moneyFmtRoundedDynamic, nfRoundedDynamic } from "utils/formatters";
import {
  getMachineTypeName,
  sortLocalMachinesFixed,
  sortTypeEntries,
} from "utils/localMachineOrder";
import { machineTypeGender, machineTypeLabel } from "utils/machineLabels";
import { hasHistoricalRent, hasRent, getRentPrice } from "utils/rent";
import { isCollectionNoticeRow, isVisitMachineCollected } from "utils/visitCollectionState";
import {
  buildMachineDisplayName,
  getLocalDisplayNumber,
  getMachineBaseName,
} from "utils/machineDisplayName";
import { formatFormulaLabel } from "utils/formula";
import { getAccumulationModeLabel } from "utils/accumulationModeLabels";

const money = (value) => moneyFmtRoundedDynamic.format(value ?? 0);
const number = (value) => nfRoundedDynamic.format(value ?? 0);
const credits = (value) => number(value);
const isRentedMachine = (machine) => hasHistoricalRent(machine) || hasRent(machine);

const cleanText = (value) => {
  const txt = String(value ?? "").trim();
  return txt || null;
};

const formatDateTimeLabel = (visit = {}) => {
  if (visit?.DateUY && visit?.TimeUY) {
    const [yyyy, mm, dd] = String(visit.DateUY).split("-");
    if (yyyy && mm && dd) return `${dd}/${mm}/${yyyy} ${visit.TimeUY}`;
    return `${visit.DateUY} ${visit.TimeUY}`;
  }

  if (visit?.DateTime) {
    const d = new Date(visit.DateTime);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
    }
  }

  return "-";
};

const formatDateOnlyLabel = (visit = {}) => {
  if (visit?.DateUY) {
    const [yyyy, mm, dd] = String(visit.DateUY).split("-");
    if (yyyy && mm && dd) return `${dd}/${mm}/${yyyy}`;
    return String(visit.DateUY);
  }

  if (visit?.DateTime) {
    const d = new Date(visit.DateTime);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  }

  return "-";
};

const toFiniteOrNull = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const hasValue = (value) => toFiniteOrNull(value) != null;

const pickFirstNumber = (machine, keys = []) => {
  for (const key of keys) {
    const value = toFiniteOrNull(machine?.[key]);
    if (value != null) return value;
  }
  return null;
};

const sumField = (items, field) =>
  (items || []).reduce((acc, item) => acc + (toFiniteOrNull(item?.[field]) ?? 0), 0);

const summaryTypeLabel = (type, count = 0) =>
  machineTypeLabel(String(type ?? "").trim(), count, { capitalize: false }).toLowerCase();

const groupTitleLabel = (type, count = 0) =>
  machineTypeLabel(String(type ?? "").trim(), count, { capitalize: true }).toUpperCase();

const rentedTypeLabel = (type, count = 0) => {
  const typeLabel = machineTypeLabel(String(type ?? "").trim(), count, {
    capitalize: false,
  }).toLowerCase();
  const gender = machineTypeGender(type);
  const rentedWord =
    gender === "m"
      ? count === 1
        ? "alquilado"
        : "alquilados"
      : count === 1
        ? "alquilada"
        : "alquiladas";

  return `${typeLabel} ${rentedWord}`;
};

const buildMachineTitle = (machine, { showDisplayNumber = false } = {}) =>
  showDisplayNumber
    ? buildMachineDisplayName(machine, { fallback: "Máquina sin nombre" })
    : getMachineBaseName(machine, "Máquina sin nombre");

const buildFormulaLabel = (machine) => {
  return formatFormulaLabel(machine, { fallback: "Sin fórmula" });
};

const compactFormulaLabel = (machine) => {
  return formatFormulaLabel(machine, { fallback: "$" }).replace(/\s+/g, "");
};

const buildTragamonedaTitle = (machine) => {
  const machineName = getMachineBaseName(machine, "Máquina sin nombre");
  const displayNumber = getLocalDisplayNumber(machine);
  const baseTitle = displayNumber == null ? machineName : `${displayNumber} ${machineName}`;
  const usesFormula = cleanText(machine?.FormulaType) != null || machine?.FormulaId != null;
  const configSuffix = [
    machine?.IsAccumulative === true
      ? null
      : machine?.IsAccumulative === false
        ? "borrado"
        : getAccumulationModeLabel(machine?.IsAccumulative),
    usesFormula ? compactFormulaLabel(machine) : "$",
  ]
    .filter(Boolean)
    .join(" ");

  return configSuffix ? `${baseTitle} - ${configSuffix}` : baseTitle;
};

const buildResetLines = (machine) => {
  if (machine?.HasReset !== true) return [];

  const lines = [{ label: "Reset", value: "si" }];
  if (hasValue(machine?.PostResetStartIn)) {
    lines.push({ label: "Post-reset entrada", value: number(machine.PostResetStartIn) });
  }
  if (hasValue(machine?.PostResetStartOut)) {
    lines.push({ label: "Post-reset salida", value: number(machine.PostResetStartOut) });
  }
  return lines;
};

const buildNoteLines = (machine) => {
  const note = cleanText(machine?.Note ?? machine?.note);
  return note ? [`Nota ${note}`] : [];
};

const buildRow = (label, inValue, outValue) => ({
  label,
  inValue,
  outValue,
});

const buildPostResetRow = (machine, usesFormula) => {
  if (machine?.WasCollected !== true || machine?.HasReset !== true) return null;

  const formatter = usesFormula ? credits : money;
  const startIn = toFiniteOrNull(machine?.PostResetStartIn);
  const startOut = toFiniteOrNull(machine?.PostResetStartOut);

  return buildRow(
    "Post-reset",
    startIn == null ? "-" : formatter(startIn),
    startOut == null ? "-" : formatter(startOut)
  );
};

const maybePushRow = (rows, label, entryValue, exitValue, formatter) => {
  if (entryValue == null && exitValue == null) return;
  rows.push(
    buildRow(
      label,
      entryValue == null ? "-" : formatter(entryValue),
      exitValue == null ? "-" : formatter(exitValue)
    )
  );
};

const buildTragamonedaMachine = (machine) => {
  const usesFormula = cleanText(machine?.FormulaType) != null || machine?.FormulaId != null;

  const rows = [];
  if (machine?.IsAccumulative === false && usesFormula) {
    rows.push(
      buildRow(
        "Actual",
        toFiniteOrNull(machine?.NetCreditsIn) == null ? "-" : credits(machine.NetCreditsIn),
        toFiniteOrNull(machine?.NetCreditsOut) == null ? "-" : credits(machine.NetCreditsOut)
      ),
      buildRow(
        "Actual",
        toFiniteOrNull(machine?.NetMoneyIn) == null ? "-" : money(machine.NetMoneyIn),
        toFiniteOrNull(machine?.NetMoneyOut) == null ? "-" : money(machine.NetMoneyOut)
      )
    );
  } else if (machine?.IsAccumulative === false) {
    rows.push(
      buildRow(
        "Actual",
        toFiniteOrNull(machine?.NetMoneyIn) == null ? "-" : money(machine.NetMoneyIn),
        toFiniteOrNull(machine?.NetMoneyOut) == null ? "-" : money(machine.NetMoneyOut)
      )
    );
  } else if (usesFormula) {
    maybePushRow(
      rows,
      "Actual",
      toFiniteOrNull(machine?.CreditsIn),
      toFiniteOrNull(machine?.CreditsOut),
      credits
    );
    maybePushRow(
      rows,
      "Anterior",
      toFiniteOrNull(machine?.PrevCreditsIn),
      toFiniteOrNull(machine?.PrevCreditsOut),
      credits
    );
    maybePushRow(
      rows,
      "Neto",
      toFiniteOrNull(machine?.NetMoneyIn),
      toFiniteOrNull(machine?.NetMoneyOut),
      money
    );
  } else {
    maybePushRow(
      rows,
      "Actual",
      toFiniteOrNull(machine?.MoneyIn),
      toFiniteOrNull(machine?.MoneyOut),
      money
    );
    maybePushRow(
      rows,
      "Anterior",
      toFiniteOrNull(machine?.PrevMoneyIn),
      toFiniteOrNull(machine?.PrevMoneyOut),
      money
    );
    maybePushRow(
      rows,
      "Neto",
      toFiniteOrNull(machine?.NetMoneyIn),
      toFiniteOrNull(machine?.NetMoneyOut),
      money
    );
  }

  const postResetRow = buildPostResetRow(machine, usesFormula);

  const totals = [];
  const collectedAmount = toFiniteOrNull(machine?.MoneyCollected);
  const collectedValue =
    collectedAmount == null
      ? null
      : collectedAmount < 0
        ? `(-${money(Math.abs(collectedAmount))})`
        : money(collectedAmount);

  if (collectedValue) {
    totals.push({ label: "Total", value: collectedValue });
  }

  return {
    type: "Tragamoneda",
    title: buildTragamonedaTitle(machine),
    configLine: null,
    status: null,
    rows,
    valueLines: totals,
    totalAmount: collectedAmount,
    resetLines: postResetRow ? [] : buildResetLines(machine),
    noteLines: buildNoteLines(machine),
  };
};

const buildPoolTejoMachine = (machine, type) => {
  const fichaCount = pickFirstNumber(machine, ["CreditsIn", "actualCreditsIn"]);
  const pricePerFicha = pickFirstNumber(machine, ["FormulaValue"]);
  const moneyIn = pickFirstNumber(machine, [
    "MoneyCollected",
    "MoneyIn",
    "moneyCollected",
    "moneyIn",
  ]);
  const fichaValue = fichaCount == null ? "-" : number(fichaCount);
  const priceValue = pricePerFicha == null ? "-" : money(pricePerFicha);

  return {
    type,
    title: buildMachineTitle(machine),
    configLine: null,
    status: null,
    rows: [],
    valueLines: [
      { label: "Fichas", value: `${fichaValue} | C/u ${priceValue}` },
      { label: "Total", value: moneyIn == null ? "-" : money(moneyIn) },
    ],
    totalAmount: moneyIn,
    resetLines: buildResetLines(machine),
    noteLines: buildNoteLines(machine),
    sourceFields: {
      fichaCount: "CreditsIn",
      pricePerFicha: "FormulaValue",
      moneyIn: moneyIn === toFiniteOrNull(machine?.MoneyCollected) ? "MoneyCollected" : "MoneyIn",
    },
  };
};

const buildPelucheraMachine = (machine) => {
  const fichaCount = pickFirstNumber(machine, ["CreditsIn", "actualCreditsIn"]);
  const pricePerFicha = pickFirstNumber(machine, ["FormulaValue"]);
  const moneyIn = pickFirstNumber(machine, ["MoneyIn", "moneyIn"]);
  const prizeCount = pickFirstNumber(machine, ["CreditsOut", "actualCreditsOut"]);
  const pricePerPrize = pickFirstNumber(machine, ["NetCreditsOut", "netCreditsOut"]);
  const moneyOut = pickFirstNumber(machine, ["MoneyOut", "moneyOut"]);
  const moneyCollected = pickFirstNumber(machine, ["MoneyCollected", "moneyCollected"]);
  const fichaValue = fichaCount == null ? "-" : number(fichaCount);
  const priceFichaValue = pricePerFicha == null ? "-" : money(pricePerFicha);
  const moneyInValue = moneyIn == null ? "-" : money(moneyIn);
  const prizeValue = prizeCount == null ? "-" : number(prizeCount);
  const pricePrizeValue = pricePerPrize == null ? "-" : money(pricePerPrize);
  const moneyOutValue = moneyOut == null ? "-" : money(moneyOut);

  return {
    type: "Peluchera",
    title: buildMachineTitle(machine),
    configLine: null,
    status: null,
    rows: [],
    valueLines: [
      {
        label: "Fichas",
        value: `${fichaValue}. C/u ${priceFichaValue} | Ingreso ${moneyInValue}`,
      },
      {
        label: "Premios",
        value: `${prizeValue}. C/u ${pricePrizeValue} | Costo premios ${moneyOutValue}`,
      },
      { label: "Total", value: moneyCollected == null ? "-" : money(moneyCollected) },
    ],
    totalAmount: moneyCollected,
    resetLines: buildResetLines(machine),
    noteLines: buildNoteLines(machine),
    sourceFields: {
      fichaCount: "CreditsIn",
      pricePerFicha: "FormulaValue",
      moneyIn: "MoneyIn",
      prizeCount: "CreditsOut",
      pricePerPrize: "NetCreditsOut",
      moneyOut: "MoneyOut",
      moneyCollected: "MoneyCollected",
    },
  };
};

const buildRockolaMachine = (machine) => {
  const income = pickFirstNumber(machine, [
    "MoneyCollected",
    "MoneyIn",
    "moneyCollected",
    "moneyIn",
  ]);

  return {
    type: "Rockola",
    title: buildMachineTitle(machine),
    configLine: null,
    status: null,
    rows: [],
    valueLines: [{ label: "Total", value: income == null ? "-" : money(income) }],
    totalAmount: income,
    resetLines: buildResetLines(machine),
    noteLines: buildNoteLines(machine),
    sourceFields: {
      income: income === toFiniteOrNull(machine?.MoneyCollected) ? "MoneyCollected" : "MoneyIn",
    },
  };
};

const buildRentMachine = (machine, type) => {
  const rentAmount = getRentPrice(machine);
  const rawType = cleanText(type) || cleanText(getMachineTypeName(machine));
  const realType = rawType ? machineTypeLabel(rawType, 1) : "Sin tipo";
  const baseTitle = buildMachineTitle(machine);
  const rentValue = rentAmount == null ? "-" : money(rentAmount);

  return {
    type,
    title: `${realType} - ${baseTitle}`,
    configLine: null,
    status: null,
    rows: [],
    valueLines: [{ label: "Alquiler", value: rentValue }],
    resetLines: buildResetLines(machine),
    noteLines: buildNoteLines(machine),
  };
};

const buildNotCollectedMachine = (machine, type) => ({
  type,
  title: buildMachineTitle(machine, {
    showDisplayNumber: type === "Tragamoneda" && !isRentedMachine(machine),
  }),
  configLine: null,
  status: "NO RECAUDADA",
  rows: [],
  valueLines: [],
  resetLines: buildResetLines(machine),
  noteLines: buildNoteLines(machine),
});

const buildMachineModel = (machine, type) => {
  const collected = isVisitMachineCollected(machine);
  if (!collected) return buildNotCollectedMachine(machine, type);
  if (isRentedMachine(machine)) return buildRentMachine(machine, type);
  if (type === "Tragamoneda") return buildTragamonedaMachine(machine);
  if (type === "Pool" || type === "Tejo") return buildPoolTejoMachine(machine, type);
  if (type === "Peluchera") return buildPelucheraMachine(machine);
  if (type === "Rockola") return buildRockolaMachine(machine);

  return {
    type,
    title: buildMachineTitle(machine),
    configLine: null,
    status: null,
    rows: [],
    valueLines: hasValue(machine?.MoneyCollected)
      ? [{ label: "Total", value: money(machine.MoneyCollected) }]
      : [],
    totalAmount: toFiniteOrNull(machine?.MoneyCollected),
    resetLines: buildResetLines(machine),
    noteLines: buildNoteLines(machine),
  };
};

const buildSummary = (type, rows) => {
  const totalAmount = sumField(rows, "MoneyCollected");
  const clientAmount = sumField(rows, "ClientAmount");
  const ownerAmount = sumField(rows, "OwnerAmount");

  const eligiblePctRows = rows.filter(
    (row) =>
      !isRentedMachine(row) &&
      row?.WasCollected !== false &&
      row?.ClientPercentage != null &&
      row?.OwnerPercentage != null
  );
  const uniquePairs = Array.from(
    new Set(eligiblePctRows.map((row) => `${row.ClientPercentage}|${row.OwnerPercentage}`))
  );
  const hasRentedRows = rows.some((row) => isRentedMachine(row));
  const showSinglePercentage = uniquePairs.length === 1 && !hasRentedRows;
  const [singleClientPct, singleOwnerPct] = showSinglePercentage
    ? uniquePairs[0].split("|").map((value) => number(value))
    : [null, null];

  return {
    type,
    typeLabel: summaryTypeLabel(type, rows.length),
    itemCount: rows.length,
    totalAmount,
    clientAmount,
    ownerAmount,
    totalAmountLabel: money(totalAmount),
    clientAmountLabel: money(clientAmount),
    ownerAmountLabel: money(ownerAmount),
    percentageMode: showSinglePercentage
      ? "single"
      : hasRentedRows || uniquePairs.length > 1
        ? "mixed_or_rent"
        : "plain",
    clientPercentageLabel: singleClientPct,
    ownerPercentageLabel: singleOwnerPct,
  };
};

const buildRentedTypeSummaries = (rows = []) => {
  const grouped = rows.reduce((acc, row) => {
    const type = getMachineTypeName(row);
    (acc[type] ||= []).push(row);
    return acc;
  }, {});

  return sortTypeEntries(grouped).map(([type, typeRows]) => {
    const totalAmount = sumField(typeRows, "MoneyCollected");
    return {
      type,
      typeLabel: summaryTypeLabel(type, typeRows.length),
      rentedLabel: rentedTypeLabel(type, typeRows.length),
      itemCount: typeRows.length,
      totalAmount,
      totalAmountLabel: money(totalAmount),
    };
  });
};

export default function buildCollectionTicketModel(detailData = {}) {
  const visit = detailData?.visit || {};
  const visitWithCollection = detailData?.visitWithCollection || {};
  const local = detailData?.local || {};
  const person = detailData?.person || null;

  const rawMachines = Array.isArray(detailData?.machines) ? detailData.machines : [];
  const filteredMachines = sortLocalMachinesFixed(rawMachines, {
    preferLocalDisplayNumber: true,
  }).filter((machine) => !isCollectionNoticeRow(machine));
  const rentedMachines = filteredMachines.filter((machine) => isRentedMachine(machine));
  const normalMachines = filteredMachines.filter((machine) => !isRentedMachine(machine));

  const grouped = normalMachines.reduce((acc, machine) => {
    const type = getMachineTypeName(machine);
    (acc[type] ||= []).push(machine);
    return acc;
  }, {});

  const machineGroups = sortTypeEntries(grouped).map(([type, rows]) => ({
    type,
    title: `==== ${groupTitleLabel(type, rows.length)} ====`,
    machines: rows.map((machine) => buildMachineModel(machine, type)),
    summary: buildSummary(type, rows),
  }));

  if (rentedMachines.length > 0) {
    machineGroups.push({
      type: "Alquiladas",
      title: "==== ALQUILADAS ====",
      machines: rentedMachines.map((machine) =>
        buildRentMachine(machine, getMachineTypeName(machine))
      ),
      summary: {
        ...buildSummary("Alquiladas", rentedMachines),
        percentageMode: "rented_by_type",
        clientPercentageLabel: null,
        ownerPercentageLabel: null,
        rentedTypeSummaries: buildRentedTypeSummaries(rentedMachines),
      },
    });
  }

  const totalsByMachineType = machineGroups.map((group) => group.summary);
  const fallbackTotals = {
    totalAmount: sumField(
      filteredMachines.filter((row) => row?.WasCollected !== false),
      "MoneyCollected"
    ),
    ownerAmount: sumField(
      filteredMachines.filter((row) => row?.WasCollected !== false),
      "OwnerAmount"
    ),
    clientAmount: sumField(
      filteredMachines.filter((row) => row?.WasCollected !== false),
      "ClientAmount"
    ),
  };
  const generalTotals = {
    totalAmount: toFiniteOrNull(visitWithCollection?.TotalAmount) ?? fallbackTotals.totalAmount,
    ownerAmount:
      toFiniteOrNull(visitWithCollection?.TotalOwnerAmount) ?? fallbackTotals.ownerAmount,
    clientAmount:
      toFiniteOrNull(visitWithCollection?.TotalClientAmount) ?? fallbackTotals.clientAmount,
  };

  return {
    header: {
      localName: cleanText(local?.Name) || cleanText(visit?.LocalName) || "-",
      ownerName: cleanText(person?.Name) || "-",
      dateLabel: formatDateTimeLabel(visit),
      dateOnlyLabel: formatDateOnlyLabel(visit),
      visitId: visitWithCollection?.Id ?? visit?.Id ?? null,
      collectionKind:
        cleanText(visitWithCollection?.CollectionKind ?? visit?.CollectionKind) || null,
    },
    local: {
      Id: local?.Id ?? visit?.LocalId ?? null,
      Name: cleanText(local?.Name),
      Address: cleanText(local?.Address),
      City: cleanText(local?.City),
      Department: cleanText(local?.Department),
    },
    person: person
      ? {
          Id: person?.Id ?? null,
          Name: cleanText(person?.Name),
          Contact: cleanText(person?.Contact),
          Account: cleanText(person?.Account),
          Address: cleanText(person?.Address),
          City: cleanText(person?.City),
          Type: cleanText(person?.Type),
          Department: cleanText(person?.Department),
        }
      : null,
    machineGroups,
    totalsByMachineType,
    generalTotals: {
      ...generalTotals,
      totalAmountLabel: money(generalTotals.totalAmount),
      ownerAmountLabel: money(generalTotals.ownerAmount),
      clientAmountLabel: money(generalTotals.clientAmount),
    },
    meta: {
      machineTypeOrder: ["Tragamoneda", "Pool", "Rockola", "Tejo", "Peluchera"],
      filteredNoticeRows: rawMachines.length - filteredMachines.length,
      machineCount: filteredMachines.length,
    },
  };
}
